import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';
import { CadsProcessingSpinnerComponent } from '../cads-processing-spinner/cads-processing-spinner.component';
import type { CadsProcessingSpinnerConfig } from '../cads-processing-spinner/cads-processing-spinner.config';
import type { CadsAppButtonConfig } from './cads-app-button.config';

/**
 * Primary call-to-action control: pill shape, fixed height, optional leading/trailing sprite icons, full-width or
 * content-hugging width. Uses CADS sprite icons ({@link CadsIconViewBitComponent}); standalone—not composed from other
 * CADS layout components. When placed inside {@link CadsMainTemplateComponent}'s footer (under `.cads-main-template__footer-actions`,
 * typically inside the `[cads-main-footer]` wrapper), the host uses intrinsic width so multiple buttons share one row.
 */
@Component({
    selector: 'cads-app-button',
    imports: [CadsIconViewBitComponent, CadsProcessingSpinnerComponent],
    templateUrl: './cads-app-button.component.html',
    styleUrl: './cads-app-button.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-app-button-host',
        '[class.cads-app-button-host--hug]': 'config().hug ?? false',
    }
})
export class CadsAppButtonComponent {
  readonly config = input.required<CadsAppButtonConfig>();

  /** Emitted on click when the control is not disabled. */
  readonly tapped = output<void>();

  readonly _buttonClass = computed(() => {
    const s = this.config().style ?? 'primary';
    return `cads-app-button cads-app-button--${s}`;
  });

  readonly _leadingIconConfig = computed((): CadsIconViewBitConfig | null => {
    const icon = this.config().leadingIcon?.trim();
    if (!icon) {
      return null;
    }
    return this._iconConfig(icon);
  });

  readonly _trailingIconConfig = computed((): CadsIconViewBitConfig | null => {
    const icon = this.config().trailingIcon?.trim();
    if (!icon) {
      return null;
    }
    return this._iconConfig(icon);
  });

  readonly _isDisabled = computed(
    () => (this.config().disabled ?? false) || (this.config().processing ?? false),
  );

  readonly _processingSpinnerConfig: CadsProcessingSpinnerConfig = {
    accessibleLabel: '',
    color: 'currentColor',
  };

  protected _onClick(): void {
    if (this._isDisabled()) {
      return;
    }
    this.tapped.emit();
  }

  private _iconConfig(icon: string): CadsIconViewBitConfig {
    return {
      icon,
      shape: 'icon',
      size: 'medium',
      collapsed: true,
      iconSizePx: 18,
      iconColor: 'currentColor',
      color: 'var(--cads-color-mid-gray)',
    };
  }
}
